<template>
  <div class="cip">
    <span v-if="label" class="cip__label">{{ label }}</span>
    <div class="cip__preview">
      <img v-if="previewSrc" :src="previewSrc" alt="">
      <span v-else class="cip__empty">—</span>
    </div>
    <label class="cip__file">
      <input type="file" accept="image/*" @change="onPick" />
      <span class="cip__file-text">{{ fileName || browseLabel }}</span>
      <md-icon class="cip__file-icon">attach_file</md-icon>
    </label>
    <CustomButton v-if="showUpload" variant="primary" :label="uploaded ? uploadedLabel : uploadLabel" :disabled="!hasNewFile" @click="$emit('upload')" />
    <span v-else-if="hasNewFile" class="cip__pending">{{ pendingLabel }}</span>
  </div>
</template>

<script>
import CustomButton from './CustomButton.vue';

/**
 * Reusable image upload card: preview + file pick + upload button.
 * - `preview` prop: URL/data-URI of the currently-saved image (shown until a new pick).
 * - On pick: reads the file to a data-URI, shows it as the new preview, and emits
 *   `change` with { file, dataUrl } so the parent can store it for upload.
 * - `upload` event fires when the user clicks the upload button.
 * - `showUpload: false` hides that button: the parent uploads the picked file
 *   with its own Save (staged mode); `pendingLabel` is shown until then.
 */
export default {
  name: 'CustomImagePicker',
  components: { CustomButton },
  props: {
    preview: { type: String, default: null },     // current saved image src
    label: { type: String, default: '' },
    browseLabel: { type: String, default: 'Choose file' },
    uploadLabel: { type: String, default: 'Upload' },
    uploadedLabel: { type: String, default: 'Uploaded ✓' },
    showUpload: { type: Boolean, default: true },
    pendingLabel: { type: String, default: 'Saved with "Save"' }
  },
  data() {
    return {
      pickedDataUrl: null,
      fileName: '',
      hasNewFile: false,
      uploaded: false
    };
  },
  computed: {
    previewSrc() {
      // A freshly-picked file takes precedence over the saved preview.
      return this.pickedDataUrl || this.preview || null;
    }
  },
  methods: {
    onPick(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.fileName = file.name;
      this.uploaded = false; // a new pick re-enables the upload button
      const reader = new FileReader();
      reader.onload = (e) => {
        this.pickedDataUrl = e.target.result;
        this.hasNewFile = true;
        this.$emit('change', { file, dataUrl: e.target.result });
      };
      reader.readAsDataURL(file);
    },
    // Called by the parent after a successful upload: disable the button (nothing
    // new to upload) and mark it as uploaded, keeping the picked image as preview.
    markUploaded() {
      this.hasNewFile = false;
      this.uploaded = true;
    },
    // Staged mode: parent uploaded the file via its Save; drop the "pending" note.
    clearPending() {
      this.hasNewFile = false;
      this.fileName = '';
    }
  }
};
</script>

<style scoped>
.cip__pending { font-size: 0.78rem; color: #b26a00; font-weight: 600; }
.cip {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  background: #fafafa;
  border: 1px solid #e3e3e3;
  border-radius: 12px;
  padding: 1rem;
}
.cip__label { font-size: 0.78rem; font-weight: 600; color: #555; }
.cip__preview {
  width: 100%;
  height: 170px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #ececec;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0.5rem;
  box-sizing: border-box;
}
.cip__preview img { max-width: 100%; max-height: 100%; object-fit: contain; }
.cip__empty { color: #c4c4c4; font-size: 1.5rem; }

.cip__file {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  background: #fff;
  padding: 0 0.7rem;
  height: 38px;
  cursor: pointer;
  overflow: hidden;
}
.cip__file input[type="file"] { display: none; }
.cip__file-text {
  flex: 1;
  font-size: 0.82rem;
  color: #5b626b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cip__file-icon { font-size: 18px !important; color: #888 !important; }
</style>
